/**
 * Initialize the one time verification code flow.
 *
 * Automatically sends the first OTP on page load
 * and wires up the "Resend it!" click handler.
 *
 * @name initializeOneTimeVerificationCode
 */
function initializeOneTimeVerificationCode() {
	if (!this.initiateOtpUrl) {
		console.error('OTP initiate URL not configured');
		this.showStepErrors([new Error('Verification is temporarily unavailable.')]);
		return;
	}

	const resendLink = document.querySelector('.js-resend-verification-code');
	const email = this.collect().get('client_email');
	// #6332 — logged-in planners have no email input; the server derives the
	// address from the session when this hidden flag is set.
	const useAccountEmail = this.collect().get('otp_use_account_email') === '1';

	if (!email && !useAccountEmail) {
		console.warn('OTP init skipped: email is empty or not found.');
		return;
	}

	let isSending = false;

	const setResendState = (text, disabled) => {
		if (!resendLink) return;

		resendLink.textContent = text;
		resendLink.style.pointerEvents = disabled ? 'none' : '';
		resendLink.style.opacity = disabled ? '0.6' : '';
	};

	const sendOtp = () => {
		if (isSending) {
			return;
		}

		isSending = true;
		setResendState('Sending…', true);

		const request = new URLSearchParams(useAccountEmail ? {} : {
			email: email,
		});

		fetch(this.initiateOtpUrl, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
			},
			body: request,
		})
			.then(async response => {
				let data = null;
				try {
					data = await response.json();
				} catch (_) {
					console.warn('OTP initiate response was not JSON:', response.status, response.statusText);
				}

				if (!response.ok) {
					const error = new Error(data?.error ?? 'Unable to send code, please wait before trying again.');
					error.code = response.status;
					throw error;
				}

				return data;
			})
			.then(data => {
				if (!data) {
					isSending = false;
					setResendState('Resend it!', false);
					return;
				}

				let message = data.message || '(wait a minute)';
				setResendState(message, true);

				// UX-only cooldown (backend enforces real limits)
				const cooldownMs = (data.cooldown_seconds || 60) * 1000;
				setTimeout(() => {
					isSending = false;
					setResendState('Resend it!', false);
				}, cooldownMs);
			})
			.catch(error => {
				console.error('OTP initiate error:', error);

				isSending = false;
				setResendState('Resend it!', false);
				this.showStepErrors([new Error(error.message ?? 'Unable to send the verification code. Please check your connection and try again.')]);
			});
	};

	// 1) Auto-trigger first OTP on page load
	sendOtp();

	// 2) Wire up resend click (remove prior listener to prevent duplicates on re-init)
	const handleResendClick = event => {
		event.preventDefault();
		sendOtp();
	};

	if (resendLink) {
		resendLink.removeEventListener('click', resendLink._otpResendHandler);
		resendLink.addEventListener('click', handleResendClick);
		resendLink._otpResendHandler = handleResendClick;
	}
}

export {
	initializeOneTimeVerificationCode
};
